/**
 * `@crm/api-client` — the one HTTP client every TypeScript surface uses (web,
 * Expo, the extension's service worker).
 *
 * It is an `OpenAPILink` rather than an `RPCLink` on purpose: the server speaks
 * the REST shape described by the emitted `openapi.json`, which is also what the
 * Swift client is generated from. One wire format for four clients means a
 * routing or serialization bug cannot hide from three of them (design §3).
 *
 * Four cross-cutting concerns live here so no caller has to remember them:
 *
 *  1. **Auth** — delegated to a `CrmAuth` adapter (`./auth.ts`).
 *  2. **`X-Correlation-Id`** — a ULID minted per logical call and echoed in every
 *     server log line, per design §3's conventions.
 *  3. **`Idempotency-Key`** — injected on every non-GET request. Minted once per
 *     logical call, so all retries of one mutation carry the SAME key and the
 *     server's `(tenant_id, idempotency_key)` store collapses them.
 *  4. **Retry** — jittered exponential backoff on retryable failures, which is
 *     only safe because of (3).
 */

import type { AppContract, AppError } from '@crm/contracts';
import { contract, isErrorCode, orpcCodeForErrorCode } from '@crm/contracts';
import { createORPCClient, ORPCError } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { ulid } from 'ulid';
import type { AuthHeaders, CrmAuth } from './auth.ts';
import { noAuth } from './auth.ts';
import type { RetryOptions } from './retry.ts';
import { backoffDelay, isRetryable, resolveRetryOptions } from './retry.ts';

/**
 * Per-call overrides, passed as the oRPC client context:
 * `client.contacts.create(input, { context: { idempotencyKey } })`.
 *
 * Every field is optional, so no call site is ever forced to pass a context.
 */
export type CrmClientContext = {
  /** Extra headers for this call. Lowercase keys; merged last, so they win. */
  readonly headers?: Readonly<Record<string, string>>;
  /**
   * Reuse a key from a previous attempt to make a retry-after-crash idempotent.
   * Omit and one is generated per call.
   */
  readonly idempotencyKey?: string;
  /** Tie this call to a correlation id you already logged. */
  readonly correlationId?: string;
  /** Skip retrying for this call (e.g. a user-cancellable action). */
  readonly noRetry?: boolean;
};

export type CrmClient = ContractRouterClient<AppContract, CrmClientContext>;

export type AuthErrorInfo = {
  readonly status: number;
  readonly authKind: CrmAuth['kind'];
  readonly correlationId: string;
  readonly path: readonly string[];
  readonly error: unknown;
};

export type CreateCrmClientOptions = {
  /** Origin of the API, e.g. `https://api.crm.example`. A path prefix is kept. */
  readonly baseUrl: string;
  /** Credential adapter. Defaults to `noAuth()`. */
  readonly auth?: CrmAuth;
  /** Override the global `fetch` (tests, RN polyfills, an instrumented fetch). */
  readonly fetch?: typeof globalThis.fetch;
  /**
   * Called on 401 / `device_revoked`. The place to clear a stale token and send
   * the user back to sign-in. It must not throw — the original error is still
   * raised to the caller either way.
   */
  readonly onAuthError?: (info: AuthErrorInfo) => void | Promise<void>;
  /** Override how `Idempotency-Key` values are minted. Must be >= 8 chars. */
  readonly idempotencyKeyFactory?: () => string;
  /** Retry policy. `{ maxRetries: 0 }` disables it. */
  readonly retry?: RetryOptions;
  /** `X-Client`, per design §3. */
  readonly client?: 'web' | 'mobile' | 'macos' | 'extension';
  /** `X-Client-Version`, which drives the extension min-version gate. */
  readonly clientVersion?: string;
};

/** Resolved once per logical call and reused by every attempt of that call. */
type CallIdentity = {
  readonly correlationId: string;
  readonly idempotencyKey: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly noRetry: boolean;
};

const CALL_IDENTITY = Symbol('crm.callIdentity');

type ResolvedContext = CrmClientContext & { readonly [CALL_IDENTITY]?: CallIdentity };

function identityOf(context: unknown): CallIdentity | undefined {
  if (typeof context !== 'object' || context === null) {
    return undefined;
  }
  return (context as ResolvedContext)[CALL_IDENTITY];
}

/** Joins a base URL and keeps any path prefix on it (`https://host/api`). */
function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

/**
 * Relabels `application/problem+json` as `application/json`.
 *
 * oRPC's response deserializer switches on the content type and only parses
 * `application/json`; anything else comes back as a `Blob`. RFC 7807 mandates
 * `application/problem+json` (design §3), so without this relabelling EVERY
 * error body reaches the decoder as an unparsed blob and the error contract is
 * silently lost. The bytes are unchanged — this only corrects a media type the
 * deserializer does not recognise as the JSON it is.
 */
function asJsonContentType(response: Response): Response {
  const contentType = response.headers.get('content-type');
  if (contentType === null || !contentType.includes('problem+json')) {
    return response;
  }
  const headers = new Headers(response.headers);
  headers.set('content-type', contentType.replace('application/problem+json', 'application/json'));
  return new Response(response.body, {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

/** Recognises the server's `AppError` / `ProblemDetails` body. */
function asAppErrorBody(body: unknown): AppError | null {
  if (typeof body !== 'object' || body === null) {
    return null;
  }
  const candidate = body as Partial<AppError> & { title?: unknown };
  if (typeof candidate.code !== 'string' || !isErrorCode(candidate.code)) {
    return null;
  }
  return {
    code: candidate.code,
    message:
      typeof candidate.message === 'string'
        ? candidate.message
        : typeof candidate.title === 'string'
          ? candidate.title
          : candidate.code,
    retryable: candidate.retryable === true,
    correlationId: typeof candidate.correlationId === 'string' ? candidate.correlationId : '',
    ...(candidate.details === undefined ? {} : { details: candidate.details }),
  };
}

export function createCrmClient(options: CreateCrmClientOptions): CrmClient {
  const auth = options.auth ?? noAuth();
  const retry = resolveRetryOptions(options.retry);
  const url = normalizeBaseUrl(options.baseUrl);
  const baseFetch = options.fetch ?? globalThis.fetch;
  const mintIdempotencyKey = options.idempotencyKeyFactory ?? ulid;

  const link = new OpenAPILink<CrmClientContext>(contract, {
    url,

    /**
     * Runs on every attempt, so `auth.headers` is re-consulted and a token that
     * expired between attempts is refreshed. The correlation and idempotency
     * values come off the per-call identity instead, so they stay pinned.
     */
    async headers(callOptions, path) {
      const identity = identityOf(callOptions.context);
      const authHeaders: AuthHeaders = await auth.headers({
        // Placeholder info: adapters use `headers()` only for the credential
        // itself. DPoP proofs are minted in the `fetch` handler below, where the
        // real method and URL exist — see `CrmAuth.signProof`.
        method: 'POST',
        url: `${url}/${path.join('/')}`,
      });
      return {
        ...authHeaders,
        ...(options.client === undefined ? {} : { 'x-client': options.client }),
        ...(options.clientVersion === undefined
          ? {}
          : { 'x-client-version': options.clientVersion }),
        ...(identity === undefined ? {} : { 'x-correlation-id': identity.correlationId }),
        ...(identity?.headers ?? {}),
      };
    },

    fetch: async (request, init) => {
      // `credentials` cannot be set on an already-built Request, so the cookie
      // adapter's mode is applied by rebuilding it here. Inert under Node.
      let finalRequest =
        auth.credentials === undefined
          ? request
          : new Request(request, { credentials: auth.credentials });
      if (auth.signProof !== undefined) {
        // The ONLY correct place to mint a DPoP proof: the request that will go
        // on the wire is visible here, so `htm`/`htu` bind to reality. The
        // server rejects anything else (apps/api/src/dpop.ts), and this runs on
        // every attempt so a retry gets a fresh `jti` instead of replaying one.
        const proof = await auth.signProof({
          method: finalRequest.method,
          url: finalRequest.url,
        });
        if (proof !== null && proof !== undefined && proof !== '') {
          finalRequest = new Request(finalRequest);
          finalRequest.headers.set('dpop', proof);
        }
      }
      return asJsonContentType(await baseFetch(finalRequest, init));
    },

    /**
     * Turns the server's RFC 7807 body into a typed error.
     *
     * Without this, oRPC's default decoder does not recognise our body — it is
     * `ProblemDetails`, not oRPC's `{code,status,message,data}` envelope — and
     * wraps the whole raw response instead. Callers would then get an opaque
     * error with no `code`, no `retryable` and no `correlationId`, which is
     * exactly the information the error contract exists to carry.
     */
    customErrorResponseBodyDecoder: (body, response) => {
      const problem = asAppErrorBody(body);
      if (problem === null) {
        return null; // let oRPC try its own decoding
      }
      return new ORPCError(orpcCodeForErrorCode(problem.code), {
        status: response.status,
        message: problem.message,
        // `data` IS the AppError, so `error.data.code` / `.retryable` /
        // `.correlationId` work — and `isRetryable` can trust the server.
        data: problem,
      });
    },

    /**
     * The retry loop and the per-call identity. Sits at the OUTERMOST layer so a
     * retry re-runs encode with the same resolved context — which is what keeps
     * the `Idempotency-Key` and `X-Correlation-Id` stable across attempts.
     */
    interceptors: [
      async (callOptions) => {
        const given = (callOptions.context ?? {}) as CrmClientContext;
        const identity: CallIdentity = {
          correlationId: given.correlationId ?? ulid(),
          idempotencyKey: given.idempotencyKey ?? mintIdempotencyKey(),
          headers: given.headers ?? {},
          noRetry: given.noRetry ?? false,
        };
        const context: ResolvedContext = { ...given, [CALL_IDENTITY]: identity };
        const attemptOptions = { ...callOptions, context };

        const budget = identity.noRetry ? 0 : retry.maxRetries;
        let attempt = 0;
        for (;;) {
          try {
            return await callOptions.next(attemptOptions);
          } catch (error) {
            if (isUnauthorized(error)) {
              // Reported before the throw so a caller that clears its token in
              // `onAuthError` has done so by the time the rejection lands.
              await reportAuthError(options.onAuthError, {
                status: statusOf(error) ?? 401,
                authKind: auth.kind,
                correlationId: identity.correlationId,
                path: callOptions.path,
                error,
              });
              throw error;
            }
            if (attempt >= budget || !isRetryable(error)) {
              throw error;
            }
            await retry.sleep(backoffDelay(attempt, retry), callOptions.signal);
            attempt += 1;
          }
        }
      },
    ],

    /**
     * `Idempotency-Key` on every mutation (design §3: "on every POST that ingests
     * client data" — applied to every non-GET here, which is strictly safer).
     * Done at this layer rather than in `headers` because it is the first place
     * the HTTP method is known.
     */
    clientInterceptors: [
      (callOptions) => {
        if (callOptions.request.method === 'GET') {
          return callOptions.next();
        }
        const identity = identityOf(callOptions.context);
        if (
          identity === undefined ||
          callOptions.request.headers['idempotency-key'] !== undefined
        ) {
          return callOptions.next();
        }
        return callOptions.next({
          ...callOptions,
          request: {
            ...callOptions.request,
            headers: {
              ...callOptions.request.headers,
              'idempotency-key': identity.idempotencyKey,
            },
          },
        });
      },
    ],
  });

  return createORPCClient<CrmClient>(link);
}

function statusOf(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

/** 401, or the `device_revoked` code, which is a 401 the device must act on. */
function isUnauthorized(error: unknown): boolean {
  if (statusOf(error) === 401) {
    return true;
  }
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && 'code' in data) {
      return (data as { code?: unknown }).code === 'device_revoked';
    }
  }
  return false;
}

async function reportAuthError(
  handler: CreateCrmClientOptions['onAuthError'],
  info: AuthErrorInfo,
): Promise<void> {
  if (handler === undefined) {
    return;
  }
  try {
    await handler(info);
  } catch {
    // A failing sign-out handler must not mask the auth error that triggered it.
  }
}
